// src/hooks/useGameVoice.js
// Peer-to-peer voice for the game table (WebRTC). Same signalling relay ('custom-voice-signal')
// and ICE/TURN setup as FloatingVoiceChat, but UI-less: the table shows only mic / speaker icons.
// Each player's mic/speaker on/off state is broadcast with 'game-voice-state' so opponents can
// see it next to their circle.
import { useState, useEffect, useRef, useCallback } from 'react';

const ICE_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: ['turn:www.jv-games.com:3478', 'turn:www.jv-games.com:3478?transport=tcp'], username: 'jvturn', credential: 'jvTurnSecret2024' }
  ]
};

export default function useGameVoice({ socket, currentUserId, players = [], roomCode = null, gameId = null, enabled = false }) {
  const [micOn, setMicOn] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [remoteStates, setRemoteStates] = useState({});

  const micRef = useRef(false);
  const speakerRef = useRef(true);
  const localStreamRef = useRef(null);
  const peersRef = useRef(new Map());
  const audioRef = useRef(new Map());

  const playerIdsKey = (players || []).map(p => String(p.userId)).sort().join(',');

  const broadcastState = useCallback((mic, spk) => {
    if (!socket || !gameId) return;
    socket.emit('game-voice-state', { gameId, micEnabled: mic, speakerEnabled: spk });
  }, [socket, gameId]);

  const sendSignal = useCallback((targetId, signal) => {
    if (socket && roomCode) socket.emit('custom-voice-signal', { code: roomCode, targetId, signal });
  }, [socket, roomCode]);

  const ensureLocalStream = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    localStreamRef.current = stream;
    stream.getAudioTracks().forEach(t => { t.enabled = false; });
    return stream;
  }, []);

  const setupPeer = useCallback((peerId) => {
    const key = String(peerId);
    if (peersRef.current.has(key)) return peersRef.current.get(key);
    const polite = String(currentUserId) < key;
    const pc = new RTCPeerConnection(ICE_CONFIG);
    const entry = { pc, polite, makingOffer: false, ignoreOffer: false };
    peersRef.current.set(key, entry);

    pc.addTransceiver('audio', { direction: 'recvonly' });
    const stream = localStreamRef.current;
    if (stream) stream.getAudioTracks().forEach(t => pc.addTrack(t, stream));

    pc.onicecandidate = (e) => {
      if (e.candidate) sendSignal(peerId, { type: 'candidate', candidate: e.candidate });
    };
    pc.ontrack = (e) => {
      let audio = audioRef.current.get(key);
      if (!audio) {
        audio = document.createElement('audio');
        audio.autoplay = true;
        audio.playsInline = true;
        audio.style.display = 'none';
        document.body.appendChild(audio);
        audioRef.current.set(key, audio);
      }
      audio.muted = !speakerRef.current;
      const remote = e.streams && e.streams[0] ? e.streams[0] : new MediaStream([e.track]);
      if (audio.srcObject !== remote) audio.srcObject = remote;
      audio.play().catch(() => {});
    };
    pc.onnegotiationneeded = async () => {
      try {
        entry.makingOffer = true;
        await pc.setLocalDescription();
        sendSignal(peerId, { type: 'offer', sdp: pc.localDescription });
      } catch (err) { /* ignore */ } finally {
        entry.makingOffer = false;
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        peersRef.current.delete(key);
      }
    };
    return entry;
  }, [currentUserId, sendSignal]);

  // Incoming WebRTC signalling (perfect-negotiation pattern, same as FloatingVoiceChat)
  useEffect(() => {
    if (!enabled || !socket) return undefined;
    const onSignal = async (data) => {
      const from = data?.from;
      const sig = data?.signal;
      if (!from || !sig) return;
      const key = String(from);
      let entry = peersRef.current.get(key);
      if (!entry) entry = setupPeer(from);
      const { pc } = entry;
      try {
        if (sig.type === 'offer') {
          const collision = entry.makingOffer || pc.signalingState !== 'stable';
          entry.ignoreOffer = !entry.polite && collision;
          if (entry.ignoreOffer) return;
          await pc.setRemoteDescription(sig.sdp);
          await pc.setLocalDescription();
          sendSignal(from, { type: 'answer', sdp: pc.localDescription });
        } else if (sig.type === 'answer') {
          await pc.setRemoteDescription(sig.sdp);
        } else if (sig.type === 'candidate') {
          if (sig.candidate) await pc.addIceCandidate(sig.candidate);
        }
      } catch (err) { /* ignore */ }
    };
    const onVoiceState = (d) => {
      if (!d || d.userId === undefined || d.userId === null) return;
      setRemoteStates(prev => ({ ...prev, [String(d.userId)]: { micEnabled: !!d.micEnabled, speakerEnabled: d.speakerEnabled !== false } }));
    };
    socket.on('custom-voice-signal', onSignal);
    socket.on('game-voice-state', onVoiceState);
    return () => {
      socket.off('custom-voice-signal', onSignal);
      socket.off('game-voice-state', onVoiceState);
    };
  }, [enabled, socket, setupPeer, sendSignal]);

  // Connect to every other player at the table, and (re)announce our own state so late joiners see it
  useEffect(() => {
    if (!enabled || !currentUserId) return;
    (players || [])
      .filter(p => String(p.userId) !== String(currentUserId))
      .forEach(p => setupPeer(p.userId));
    broadcastState(micRef.current, speakerRef.current);
  }, [enabled, currentUserId, playerIdsKey, setupPeer, broadcastState]);

  const setMicEnabled = useCallback(async (enable) => {
    try {
      const stream = await ensureLocalStream();
      stream.getAudioTracks().forEach(t => { t.enabled = enable; });
      peersRef.current.forEach(({ pc }) => {
        stream.getAudioTracks().forEach(t => {
          const sender = pc.getSenders().find(s => s.track === t);
          if (enable && !sender) pc.addTrack(t, stream);
          else if (!enable && sender) pc.removeTrack(sender);
        });
      });
      micRef.current = enable;
      setMicOn(enable);
      setPermissionDenied(false);
      if (enable) audioRef.current.forEach(el => { el.muted = !speakerRef.current; el.play().catch(() => {}); });
      broadcastState(enable, speakerRef.current);
    } catch (err) {
      setPermissionDenied(true);
    }
  }, [ensureLocalStream, broadcastState]);

  const setSpeakerEnabled = useCallback((enable) => {
    speakerRef.current = enable;
    setSpeakerOn(enable);
    audioRef.current.forEach(el => {
      el.muted = !enable;
      if (enable) el.play().catch(() => {});
    });
    broadcastState(micRef.current, enable);
  }, [broadcastState]);

  const toggleMic = useCallback(() => setMicEnabled(!micRef.current), [setMicEnabled]);
  const toggleSpeaker = useCallback(() => setSpeakerEnabled(!speakerRef.current), [setSpeakerEnabled]);

  const openAppSettings = useCallback(() => {
    const capApp = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App;
    if (capApp && typeof capApp.openAppSettings === 'function') {
      capApp.openAppSettings().catch(() => {});
      return;
    }
    if (/Android/i.test(navigator.userAgent)) {
      try { window.location.href = 'app-settings:'; } catch (e) { /* ignore */ }
      alert('Please allow Microphone access:\nSettings → Apps → JV Games → Permissions → Microphone');
      return;
    }
    alert('Please enable Microphone access in your browser/device settings, then tap the mic again.');
  }, []);

  // Cleanup everything when leaving the table
  useEffect(() => () => {
    peersRef.current.forEach(entry => { try { entry.pc.close(); } catch (e) { /* ignore */ } });
    peersRef.current.clear();
    audioRef.current.forEach(el => { try { el.srcObject = null; el.remove(); } catch (e) { /* ignore */ } });
    audioRef.current.clear();
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
  }, []);

  return { micOn, speakerOn, permissionDenied, remoteStates, toggleMic, toggleSpeaker, openAppSettings };
}

